import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import {
  Box, Typography, FormControlLabel, Switch, Stack, Button, Chip,
  CircularProgress, Table, TableBody, TableCell, TableHead, TableRow,
  IconButton, Tooltip, Alert,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SyncIcon from '@mui/icons-material/Sync';
import { toast } from 'react-toastify';
import { PAID_TIERS, parseJsonArray, withInheritedGrantTiers } from '../../Utils/frameworkTiers';
import {
  CreateEntitlementCode, UpdateEntitlementCode, GetStripeCoupon,
} from '../../Api/Billing';
import StandardInputField from '../StandardComponents/StandardInputField/StandardInputField';
import { FIELD_CONFIG, isoToDisplayDate, parseDisplayDate } from './dialogStyles';

const tierOptions = PAID_TIERS.map((t) => ({ value: t, label: t }));
const kindOptions = [
  { value: 'grant', label: 'Full unlock (grant tiers)' },
  { value: 'stripe_discount', label: 'Checkout discount (Stripe coupon)' },
];
const appliesToOptions = [
  { value: 'all', label: 'All plans (no locking)' },
  { value: 'essentials', label: 'Essentials' },
  { value: 'upgrades', label: 'Upgrades' },
];

const emptyForm = {
  code: '',
  codeKind: 'grant',
  discountAppliesTo: 'essentials',
  stripeCouponId: '',
  discountPercent: '',
  tiers: [],
  maxUses: '',
  grantDurationDays: '365',
  validUntil: '',
  isActive: true,
  notes: '',
};

const formatPromoDate = (unix) => {
  if (!unix) return '—';
  try {
    return new Date(unix * 1000).toLocaleDateString(undefined, {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch {
    return '—';
  }
};

const unixToDisplayDate = (unix) => {
  if (!unix) return '';
  const d = new Date(unix * 1000);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
};

const formatMoney = (amountCents, currency) => {
  if (amountCents == null) return '';
  const cur = String(currency || '').toUpperCase() || 'SGD';
  return `${cur} ${(Number(amountCents) / 100).toFixed(2)}`;
};

/** Derive discount_applies_to from Stripe applies_to_products. */
export const deriveDiscountAppliesTo = (stripeDetail) => {
  if (!stripeDetail) return null;
  const products = stripeDetail.applies_to_products || [];
  if (products.length === 0) return 'all';
  const tiers = products.map((p) => (p.tier || '').toLowerCase());
  const names = products.map((p) => (p.name || '').toLowerCase()).join(' ');
  const hasEssentials = tiers.some((t) => t === 'essentials') || names.includes('essentials');
  const hasUpgrade = tiers.some((t) => t && t !== 'essentials')
    || /advance|premium/.test(names);
  if (hasEssentials && !hasUpgrade) return 'essentials';
  if (hasUpgrade && !hasEssentials) return 'upgrades';
  return null;
};

const checkoutUiExplainers = (appliesTo, stripeDetail) => {
  const duration = stripeDetail?.duration || '';
  const onceNote = duration === 'once'
    ? 'One-time off (applies once to the subscription).'
    : duration
      ? `Stripe coupon duration: ${duration}${stripeDetail?.duration_in_months ? ` (${stripeDetail.duration_in_months} mo)` : ''}.`
      : null;
  const bullets = [];
  if (appliesTo === 'all') {
    bullets.push('No plan cards greyed out — any plan can be chosen.');
    bullets.push('All billing periods remain selectable.');
    bullets.push('Discount can apply to any plan at checkout.');
  } else if (appliesTo === 'essentials') {
    bullets.push('Essentials is allowed; Advance & Premium are greyed out (“Not included with this code”).');
    bullets.push('Billing period locked to Annual (Monthly / Quarterly blocked).');
    bullets.push('Price strikethrough / discount math applies to Essentials only.');
  } else if (appliesTo === 'upgrades') {
    bullets.push('Advance & Premium are allowed; Essentials is greyed out.');
    bullets.push('Billing periods remain selectable.');
    bullets.push('Price strikethrough / discount math applies to upgrade plans only.');
  }
  if (onceNote) bullets.push(onceNote);
  if (stripeDetail?.redeem_by) {
    bullets.push(`Coupon redeem-by / Valid Until: ${formatPromoDate(stripeDetail.redeem_by)}.`);
  }
  return bullets;
};

/**
 * EntitlementCodeModal — form body for create/edit unlock codes.
 * Supports full-grant codes and Stripe checkout discount codes with Sync.
 */
const EntitlementCodeModal = forwardRef(({ flag, editItems: code, onClose, getAllData }, ref) => {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [stripeDetail, setStripeDetail] = useState(null);
  const [syncError, setSyncError] = useState('');
  const isEdit = flag === 'edit';
  const isDiscount = form.codeKind === 'stripe_discount';

  useEffect(() => {
    if (isEdit && code) {
      setForm({
        code: code.code || '',
        codeKind: code.code_kind || 'grant',
        discountAppliesTo: code.discount_applies_to || 'upgrades',
        stripeCouponId: code.stripe_coupon_id || '',
        discountPercent: code.discount_percent ?? '',
        tiers: withInheritedGrantTiers(parseJsonArray(code.tiers)),
        maxUses: code.max_uses ?? '',
        grantDurationDays: code.grant_duration_days != null && code.grant_duration_days > 0
          ? String(code.grant_duration_days)
          : '365',
        validUntil: isoToDisplayDate(code.valid_until),
        isActive: code.is_active !== false,
        notes: code.notes || '',
      });
    } else {
      setForm(emptyForm);
    }
    setStripeDetail(null);
    setSyncError('');
    setSaving(false);
  }, [isEdit, code]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'tiers') {
      set({ tiers: withInheritedGrantTiers(Array.isArray(value) ? value : []) });
    } else {
      set({ [name]: value });
    }
    if (name === 'stripeCouponId' || name === 'codeKind') {
      setStripeDetail(null);
      setSyncError('');
    }
  };

  const syncStripe = useCallback(async (couponId, autofill) => {
    const id = (couponId || '').trim();
    if (!id) {
      toast.error('Enter a Stripe Coupon ID first');
      return;
    }
    setSyncing(true);
    setSyncError('');
    try {
      const res = await GetStripeCoupon(id);
      const data = res?.data?.data ?? res?.data;
      setStripeDetail(data || null);
      if (autofill && data) {
        const patch = {};
        if (data.percent_off) {
          patch.discountPercent = String(Math.round(data.percent_off));
        } else if (data.amount_off) {
          // Fixed-amount coupons: leave % empty (checkout uses coupon id).
          patch.discountPercent = '';
        }
        if (data.redeem_by) {
          const display = unixToDisplayDate(data.redeem_by);
          if (display) patch.validUntil = display;
        }
        if (data.max_redemptions) {
          patch.maxUses = String(data.max_redemptions);
        }
        const derived = deriveDiscountAppliesTo(data);
        if (derived) patch.discountAppliesTo = derived;
        if (Object.keys(patch).length) set(patch);
      }
    } catch (e) {
      setStripeDetail(null);
      const msg = e?.response?.data?.error || 'Stripe coupon not found';
      setSyncError(msg);
      toast.error(msg);
    } finally {
      setSyncing(false);
    }
  }, []);

  useEffect(() => {
    if (!isEdit || !code) return;
    if ((code.code_kind || 'grant') !== 'stripe_discount') return;
    const id = (code.stripe_coupon_id || '').trim();
    if (!id) return;
    syncStripe(id, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync once when edit opens
  }, [isEdit, code?.id]);

  useImperativeHandle(ref, () => ({
    async saveData() {
      if (!isEdit && !form.code.trim()) { toast.error('Code is required'); return false; }
      if (isDiscount) {
        if (!form.stripeCouponId.trim()) {
          toast.error('Stripe Coupon ID is required (e.g. HBFFXEZX)');
          return false;
        }
        if (!form.discountAppliesTo) {
          toast.error('Discount applies to is required');
          return false;
        }
      } else if (form.tiers.length === 0) {
        toast.error('At least one tier is required');
        return false;
      }
      if (!isDiscount) {
        const days = parseInt(form.grantDurationDays, 10);
        if (!form.grantDurationDays || Number.isNaN(days) || days < 1) {
          toast.error('Grant Days is required (default 365 for annual)');
          return false;
        }
      }

      const validUntilDate = parseDisplayDate(form.validUntil);
      const payload = {
        code_kind: form.codeKind,
        stripe_coupon_id: isDiscount ? form.stripeCouponId.trim() : null,
        discount_percent: isDiscount && form.discountPercent !== ''
          ? parseInt(form.discountPercent, 10)
          : null,
        discount_applies_to: isDiscount ? form.discountAppliesTo : null,
        tiers: isDiscount ? [] : form.tiers,
        is_active: form.isActive,
        max_uses: form.maxUses !== '' ? parseInt(form.maxUses, 10) : null,
        grant_duration_days: !isDiscount
          ? parseInt(form.grantDurationDays, 10) || 365
          : null,
        valid_until: validUntilDate ? validUntilDate.toISOString() : null,
        notes: form.notes.trim() || null,
      };

      setSaving(true);
      try {
        if (isEdit) {
          await UpdateEntitlementCode(code.id, payload);
          toast.success('Code updated');
        } else {
          await CreateEntitlementCode({ ...payload, code: form.code.trim().toUpperCase() });
          toast.success('Code created');
        }
        if (getAllData) await getAllData();
        if (onClose) onClose();
        return true;
      } catch (e) {
        toast.error(e?.response?.data?.error || (isEdit ? 'Update failed' : 'Create failed'));
        return false;
      } finally {
        setSaving(false);
      }
    },
  }));

  const scopeMismatch = (() => {
    if (!stripeDetail || !isDiscount) return null;
    const products = stripeDetail.applies_to_products || [];
    if (products.length === 0) {
      if (form.discountAppliesTo === 'all') return null;
      return 'This Stripe coupon applies to all products (no product restriction). Prefer “All plans (no locking)” or confirm the scope is intentional.';
    }
    const tiers = products.map((p) => (p.tier || '').toLowerCase());
    const names = products.map((p) => (p.name || '').toLowerCase()).join(' ');
    if (form.discountAppliesTo === 'essentials') {
      const ok = tiers.some((t) => t === 'essentials') || names.includes('essentials');
      if (!ok) return 'Discount applies to Essentials, but Stripe products do not include Zavior Essentials.';
    }
    if (form.discountAppliesTo === 'upgrades') {
      const ok = tiers.some((t) => t && t !== 'essentials')
        || /advance|premium/.test(names);
      if (!ok) return 'Discount applies to Upgrades, but Stripe products look like Essentials-only.';
    }
    if (form.discountAppliesTo === 'all') {
      return 'Discount applies to All plans, but Stripe coupon has product restrictions.';
    }
    return null;
  })();

  const explainers = isDiscount
    ? checkoutUiExplainers(form.discountAppliesTo, stripeDetail)
    : [];

  const redemptionLabel = (() => {
    if (!stripeDetail) return null;
    const used = stripeDetail.times_redeemed ?? 0;
    if (stripeDetail.max_redemptions) {
      return `Redeemed ${used} / ${stripeDetail.max_redemptions}`;
    }
    return `Redeemed ${used} (unlimited)`;
  })();

  return (
    <Box sx={{ width: '100%' }}>
      <Stack spacing={0}>
        <StandardInputField
          type="text"
          name="code"
          label="Code"
          value={form.code}
          onChange={handleChange}
          disabled={isEdit || saving}
          helperText={isEdit ? 'Code string cannot be changed after creation.' : 'Uppercased on save. Example: HPCC50'}
          config={FIELD_CONFIG}
        />
        <StandardInputField
          type="select"
          name="codeKind"
          label="Code Kind"
          value={form.codeKind}
          onChange={handleChange}
          options={kindOptions}
          config={{ ...FIELD_CONFIG, disabled: saving || isEdit }}
        />
        {isDiscount ? (
          <>
            <StandardInputField
              type="select"
              name="discountAppliesTo"
              label="Discount applies to"
              value={form.discountAppliesTo}
              onChange={handleChange}
              options={appliesToOptions}
              helperText="All = no UI locking. Essentials = base plan only (+ Annual). Upgrades = Advance / Premium."
              config={{ ...FIELD_CONFIG, disabled: saving }}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr auto' }, gap: 1.5, mb: 1, alignItems: 'start' }}>
              <StandardInputField
                type="text"
                name="stripeCouponId"
                label="Stripe Coupon ID"
                value={form.stripeCouponId}
                onChange={handleChange}
                helperText="From Stripe → Coupons (e.g. 8w7KB5dc). Not the promo_… id."
                config={{ ...FIELD_CONFIG, disabled: saving }}
              />
              <Button
                className="global-outlined-button"
                startIcon={syncing ? <CircularProgress size={14} /> : <SyncIcon />}
                onClick={() => syncStripe(form.stripeCouponId, true)}
                disabled={saving || syncing || !form.stripeCouponId.trim()}
                sx={{ textTransform: 'none', mt: { sm: 3.5 }, whiteSpace: 'nowrap' }}
              >
                Sync from Stripe
              </Button>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 2 }}>
              <StandardInputField
                type="number"
                name="discountPercent"
                label="Discount %"
                value={form.discountPercent}
                onChange={handleChange}
                helperText="UI strikethrough; leave blank for fixed-amount Stripe coupons"
                config={{ ...FIELD_CONFIG, disabled: saving }}
              />
              <StandardInputField
                type="number"
                name="maxUses"
                label="Max Uses"
                value={form.maxUses}
                onChange={handleChange}
                helperText="Blank = unlimited"
                config={{ ...FIELD_CONFIG, disabled: saving }}
              />
            </Box>
          </>
        ) : (
          <StandardInputField
            type="select"
            name="tiers"
            label="Tiers Granted"
            value={form.tiers}
            onChange={handleChange}
            options={tierOptions}
            placeholder="Select tiers"
            helperText="Selecting Premium also selects Advance in that track. Essentials/platform access is granted on redeem."
            config={{
              ...FIELD_CONFIG,
              multiple: true,
              withCheckbox: true,
              disabled: saving,
              renderValue: (selected) => {
                if (!selected?.length) return 'Select tiers';
                if (selected.length === 1) return selected[0];
                return `${selected[0]}, +${selected.length - 1} more`;
              },
            }}
          />
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: isDiscount ? '1fr' : '1fr 1fr 1fr' }, gap: 1.5, mb: 2 }}>
          {!isDiscount && (
            <>
              <StandardInputField
                type="number"
                name="maxUses"
                label="Max Uses"
                value={form.maxUses}
                onChange={handleChange}
                helperText="Blank = unlimited"
                config={{ ...FIELD_CONFIG, disabled: saving }}
              />
              <StandardInputField
                type="number"
                name="grantDurationDays"
                label="Grant Days"
                value={form.grantDurationDays}
                onChange={handleChange}
                helperText="Required. 30 ≈ monthly, 90 ≈ quarterly, 365 = annual"
                config={{ ...FIELD_CONFIG, disabled: saving, required: true }}
              />
            </>
          )}
          <StandardInputField
            type="date"
            name="validUntil"
            label="Valid Until"
            value={form.validUntil}
            onChange={handleChange}
            config={{ ...FIELD_CONFIG, disabled: saving }}
          />
        </Box>
        {isEdit && (
          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                onChange={(e) => set({ isActive: e.target.checked })}
                disabled={saving}
              />
            }
            label="Active"
            sx={{ mb: 2 }}
          />
        )}
        <StandardInputField
          type="text"
          name="notes"
          label="Internal Notes"
          value={form.notes}
          onChange={handleChange}
          helperText="Tech Admin only — never shown to redeemers."
          config={{ ...FIELD_CONFIG, multiline: true, minRows: 2, disabled: saving }}
        />

        {isDiscount && syncError ? (
          <Alert severity="error" sx={{ mb: 2 }}>{syncError}</Alert>
        ) : null}

        {isDiscount && stripeDetail ? (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: '#FAFAFA',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {stripeDetail.name || stripeDetail.id}
              </Typography>
              <Chip
                size="small"
                label={stripeDetail.valid ? 'Valid' : 'Invalid'}
                color={stripeDetail.valid ? 'success' : 'default'}
                variant="outlined"
              />
              {stripeDetail.percent_off ? (
                <Chip size="small" label={`${stripeDetail.percent_off}% off`} color="primary" variant="outlined" />
              ) : null}
              {!stripeDetail.percent_off && stripeDetail.amount_off ? (
                <Chip
                  size="small"
                  label={`Fixed: ${formatMoney(stripeDetail.amount_off, stripeDetail.currency)} off`}
                  color="primary"
                  variant="outlined"
                />
              ) : null}
              {stripeDetail.duration ? (
                <Chip
                  size="small"
                  label={
                    stripeDetail.duration_in_months
                      ? `Duration: ${stripeDetail.duration} (${stripeDetail.duration_in_months} mo)`
                      : `Duration: ${stripeDetail.duration}`
                  }
                  variant="outlined"
                />
              ) : null}
              {stripeDetail.redeem_by ? (
                <Chip size="small" label={`Expires: ${formatPromoDate(stripeDetail.redeem_by)}`} variant="outlined" />
              ) : null}
              {redemptionLabel ? (
                <Chip size="small" label={redemptionLabel} variant="outlined" />
              ) : null}
            </Stack>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              {(stripeDetail.applies_to_products || []).length === 0 ? (
                <Chip size="small" label="Applicable: all products" variant="outlined" />
              ) : (
                (stripeDetail.applies_to_products || []).map((p) => (
                  <Chip
                    key={p.id}
                    size="small"
                    label={p.name || p.tier || p.id}
                    variant="outlined"
                  />
                ))
              )}
            </Stack>
            {scopeMismatch ? (
              <Alert severity="warning" sx={{ mb: 1 }}>{scopeMismatch}</Alert>
            ) : null}

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75 }}>
              When redeemed — checkout UI
            </Typography>
            <Box component="ul" sx={{ m: 0, mb: 1.5, pl: 2.5 }}>
              {explainers.map((line) => (
                <Typography key={line} component="li" variant="body2" color="text.secondary" sx={{ mb: 0.25 }}>
                  {line}
                </Typography>
              ))}
            </Box>

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Promotion codes
            </Typography>
            {(stripeDetail.promotion_codes || []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No promotion codes. Zavior unlock codes do not require Stripe promotion codes.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Promotion Code</strong></TableCell>
                    <TableCell><strong>API ID</strong></TableCell>
                    <TableCell><strong>Redemptions</strong></TableCell>
                    <TableCell><strong>First-time</strong></TableCell>
                    <TableCell><strong>Customer</strong></TableCell>
                    <TableCell><strong>Min order</strong></TableCell>
                    <TableCell><strong>Expires</strong></TableCell>
                    <TableCell><strong>Created</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(stripeDetail.promotion_codes || []).map((pc) => (
                    <TableRow key={pc.id}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{pc.code}</TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {pc.id}
                          </Typography>
                          <Tooltip title="Copy API ID">
                            <IconButton
                              size="small"
                              onClick={() => {
                                navigator.clipboard?.writeText(pc.id);
                                toast.success('Copied');
                              }}
                            >
                              <ContentCopyIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {pc.max_redemptions
                          ? `${pc.times_redeemed ?? 0} / ${pc.max_redemptions}`
                          : (pc.times_redeemed ?? 0)}
                      </TableCell>
                      <TableCell>{pc.first_time_transaction ? 'Yes' : '—'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {pc.customer_id || '—'}
                      </TableCell>
                      <TableCell>
                        {pc.minimum_amount
                          ? formatMoney(pc.minimum_amount, pc.minimum_amount_currency)
                          : '—'}
                      </TableCell>
                      <TableCell>{formatPromoDate(pc.expires_at)}</TableCell>
                      <TableCell>{formatPromoDate(pc.created)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        ) : null}

        <Typography variant="caption" color="text.secondary">
          {isDiscount
            ? 'Discount codes arm a Stripe coupon on the next Checkout. They do not grant free tiers.'
            : 'Unlock codes grant tiers with source code (not a Stripe coupon).'}
        </Typography>
      </Stack>
    </Box>
  );
});

EntitlementCodeModal.displayName = 'EntitlementCodeModal';

export default EntitlementCodeModal;
